import { TWO_WIRE_MODE } from '@/features/operations/components/pages/Diagnostics/MapViewTwoWire/types'
import { STATION_DETAIL } from '@/types'

export enum MarkerStatus {
  Normal = 'Normal',
  Error = 'Error',
  Pending = 'Pending',
  Running = 'Running',
}

interface Marker {
  id: number
  name: string
  imageUrl: string
  s3Url: string
}

export const HIGH_VOLTAGE = 25000

export interface RunDiagnosticsParams {
  interfaceId: number
  lastStationId: number
  stationData: any[]

}

export interface VoltageItem {
  addr: number
  voltage: number
}

export type VoltagesData = VoltageItem[]

export const DiagnosticStatusErrorDataDefault: DiagnosticStatusError = {
  code: 0,
  description: '',
  detailedCode: 0,
  reason: '',
  action: '',
}

export interface DiagnosticStatusError {
  code: number
  description: string
  detailedCode: number
  reason: string
  action: string
}

interface DiagnosticStatus {
  id: number
  description: string
  decoderName: string
  status: DiagnosticStatusEnum
  error?: null | DiagnosticStatusError
  internalError: string | null
  continue: boolean
  isCompleted: boolean
}

export enum DiagnosticStatusEnum {
  Pending = 'Pending',
  Running = 'Running',
  Normal = 'Normal',
  Error = 'Error',
  Completed = 'Completed',
  Offline = 'Offline',
  Default = '',
}

export interface ExtendedStationDetail extends STATION_DETAIL {
  description?: string
  address?: number
  index?: number
  controllerValue?: number
  interfaceId?: number
  state?: number
  errorName?: null | string
  voltage?: VoltageItem
  decoderName?: string
  latitude: number
  longitude: number

  marker: Marker
  markerStationError: Marker
  markerLeak: Marker
  markerLowVoltage: Marker
  floorId: number
  isShowOnMap: boolean

  diagnosingStatus: DiagnosticStatusEnum
  diagnosticStatus: DiagnosticStatus
  decoderLeakStatus: {
    [key: string]: any
    leakData: DecoderLeaksData
  }

  controller: any
  originalId?: number
}

export interface DiagnosticProgress {
  total?: number
  completed: number
  completedStations: number[]
}

export interface IDiagnosticMapViewState {
  loading: boolean // general
  isLoadingStationError: boolean
  isLoadingLeaks: boolean
  isLoadingLowVoltage: boolean
  error: boolean | string | null
  filter: {
    floorId: number | null
    locationId: number | null
  }
  mapTwoWireMode: TWO_WIRE_MODE
  stations: ExtendedStationDetail[]
  diagnosingStations: ExtendedStationDetail[]
  floors: any[]
  diagnosticsStatus: any[]
  map: {
    center: {
      latitude: number
      longitude: number
    }
    zoom: number | null
  }
  lastRunningMode: {
    stationError: number
    sideLeak: number
    lowVoltage: number
  }
  lowVoltageDecoders: VoltagesData
  leaksData: DecoderLeaksData[]
  lastLeakScanTime: string | null
  decoderLeakStatus: {
    [stationId: string]: {
      hasLeak: boolean
      leakData?: DecoderLeaksData
    }
  }
  diagnosticProgress: DiagnosticProgress
  leaksByInterface: Record<number, InterfaceLeakState>
  voltagesByInterface: Record<number, InterfaceVoltageState>
  twoWireTestsByInterface: Record<number, InterfaceTwoWireTestState>
  stationsByInterface: Record<number, InterfaceStationDetailState[]>
  isLoadingStationByInterface: boolean
  isSupController: boolean
  selectedSubInterfaceForMapView: number[] | null
}

export interface InterfaceLeakState {
  isLoading: boolean
  leaksData: DecoderLeaksData[]
  lastLeakScanTime?: string
  decoderLeakStatus: Record<number, any>
  stations: ExtendedStationDetail[]
  getLeakStateCompleted: boolean
  isLeakStateError?: boolean
}

export interface InterfaceVoltageState {
  isLoading: boolean
  lowVoltageDecoders: VoltagesData
  stations: ExtendedStationDetail[]
  isVoltageStateError?: boolean
  getVoltageStateCompleted?: boolean
}

export interface InterfaceTwoWireTestState {
  isLoading: boolean
  twoWireTestData?: TwoWireTestData
  isTwoWireTestError?: boolean
}

export interface InterfaceStationDetailState {
  description?: string
  address?: number
  index?: number
  controllerValue?: number
  interfaceId?: number
  state?: number
  errorName?: null | string
  twiOriginalId?: number
  twiName?: string
}

export const initialState: IDiagnosticMapViewState = {
  loading: false,
  isLoadingStationError: false,
  isLoadingLeaks: false,
  isLoadingLowVoltage: false,
  error: false,

  filter: {
    floorId: null,
    locationId: null,
  },

  mapTwoWireMode: TWO_WIRE_MODE.STATION_ERROR,
  stations: [],
  floors: [],
  diagnosticsStatus: [],
  map: {
    center: {
      latitude: 1.2271,
      longitude: 103.8431,
    },
    zoom: null,
  },
  lastRunningMode: {
    stationError: 0,
    sideLeak: 0,
    lowVoltage: 0,
  },
  lowVoltageDecoders: [],

  leaksData: [],
  lastLeakScanTime: null,
  decoderLeakStatus: {},
  diagnosticProgress: {
    total: 0,
    completed: 0,
    completedStations: [],
  },
  diagnosingStations: [],
  leaksByInterface: {},
  voltagesByInterface: {},
  twoWireTestsByInterface: {},

  stationsByInterface: {},
  isLoadingStationByInterface: false,
  isSupController: false,
  selectedSubInterfaceForMapView: null,
}

// types.ts

export const diagnosticStatus: DiagnosticStatus = {
  id: 0,
  description: '',
  decoderName: '',
  status: DiagnosticStatusEnum.Default,
  error: null,
  internalError: null,
  continue: false,
  isCompleted: false,
}

export interface DecoderLeaksData {
  addr: number
  leak: number
}

export interface TwoWireTestData {
  capacityLineA: number
  capacityLineB: number
  gndLeakLineA: number
  gndLeakLineB: number
}

export const SELECT_ALL = 'ALL'
